import { isFuture, isValid, parseISO } from 'date-fns';
import { createDraftSafeSelector } from '@reduxjs/toolkit';

import { getGatesToken, getGateAuthToken } from 'modules/gates/selectors';
import { getVehicleCheckToken } from 'modules/vehicleCheck/selectors';
import { CodeLoginError, CookiesSettings, MFAError } from 'modules/auth/types/InitialState';
import {
  getContext, getLoginToken, getTimezoneByDealerId, isCodeLoginEnabled, isKioskOrigin, isPublicDevice,
} from 'modules/dealers/selectors';
import {
  ADMIN_TOKEN_ENDPOINTS,
  GATES_TOKEN_ENDPOINTS,
  LOGIN_TOKEN_ENDPOINTS,
  TOKEN_EXCLUDED_ENDPOINTS,
  GATES_ACCESS_ENDPOINTS,
} from 'constants/url';

import type { RootState } from 'App/Store';
import { ErrorTypeEnum } from '../../types/Error';
import { QR_CODE_LENGTH } from './constants';

const getRoot = (root: RootState) => root;
const getProps = (_: RootState, props: string) => props;
export const getState = (state: RootState) => state.auth;

export const getToken = createDraftSafeSelector(getState, ({ token }) => token);
export const hasToken = createDraftSafeSelector(getToken, (token) => Boolean(token));
export const getExpiresIn = createDraftSafeSelector(getState, ({ expiresIn }) => expiresIn);
export const getSelectedDealerId = createDraftSafeSelector(getState, ({ dealerId }) => dealerId);
export const getSelectedSelfServiceId = createDraftSafeSelector(getState, ({ selfServiceId }) => selfServiceId);
export const getMFAData = createDraftSafeSelector(getState, (state) => state.mfa);

export const isKioskNotPrepared = createDraftSafeSelector(
  getState,
  getMFAData,
  isKioskOrigin,
  ({ kioskPrepared }, mfa, isKiosk) => (
    kioskPrepared !== undefined ? !kioskPrepared : (isKiosk && (mfa?.mobility?.notPrepared ?? false))
  ),
);
export const getMFAError = createDraftSafeSelector(getState, (state) => state.mfaError as MFAError);
export const getLoginState = createDraftSafeSelector(getState, (state) => state.login ?? {});
export const isLoadingLogin = createDraftSafeSelector(getLoginState, (login) => login.isLoading);
export const getErrorLogin = createDraftSafeSelector(getLoginState, (login) => login.error);
export const getLoginData = createDraftSafeSelector(getLoginState, (login) => login.data ?? {});

export const hasLocationIdError = createDraftSafeSelector(
  getErrorLogin,
  (error) => error?.data?.errorType === ErrorTypeEnum.SO_WRONG_SHAREBOX_LOCATION_ID,
);
export const hasRegistrationError = createDraftSafeSelector(
  getErrorLogin,
  (error) => error?.data?.errorType === ErrorTypeEnum.SO_WRONG_REGISTRATION,
);
export const getAdminCodeAttempt = createDraftSafeSelector(getState, (state) => state.adminCodeAttempt);
export const getQRCodeToken = createDraftSafeSelector(getState, (state) => state.qrCodeToken);
export const getAdminToken = createDraftSafeSelector(getState, (state) => state.adminToken);
export const hasAdminToken = createDraftSafeSelector(getAdminToken, (token) => Boolean(token));

export const getAuthError = createDraftSafeSelector(getMFAError, getErrorLogin, (mfaError, loginError) => mfaError ?? loginError);

const getSchedule = createDraftSafeSelector(getState, (state) => state.schedule);

export const getScheduledTime = createDraftSafeSelector(
  getProps,
  getSchedule,
  (type, schedule = {}) => (schedule[type] ? parseISO(schedule[type]) : undefined),
);

export const hasScheduledTime = createDraftSafeSelector(getScheduledTime, (time) => isValid(time));

export const getAuthorisationToken = createDraftSafeSelector(
  getToken,
  getLoginToken,
  getAdminToken,
  getVehicleCheckToken,
  getGatesToken,
  getGateAuthToken,
  getProps,
  (userToken, loginToken, adminToken, vehicleCheckToken, gatesToken, gateAuthToken, endpoint) => {
    if (!TOKEN_EXCLUDED_ENDPOINTS.includes(endpoint)) {
      if (LOGIN_TOKEN_ENDPOINTS.includes(endpoint)) {
        return loginToken;
      }

      if (ADMIN_TOKEN_ENDPOINTS.includes(endpoint)) {
        return adminToken;
      }

      if (GATES_TOKEN_ENDPOINTS.includes(endpoint)) {
        return gatesToken;
      }

      if (GATES_ACCESS_ENDPOINTS.includes(endpoint)) {
        return gateAuthToken;
      }

      return vehicleCheckToken ?? userToken;
    }

    return null;
  },
);

export const getPublicToken = createDraftSafeSelector(getState, ({ publicToken }) => publicToken);

export const getVehicleCheckPublicToken = createDraftSafeSelector(
  getState,
  ({ vehicleCheckPublicToken }) => vehicleCheckPublicToken,
);

export const hasKioskRootAccess = createDraftSafeSelector(getState, ({ kioskRootAccess }) => kioskRootAccess ?? false);

export const getEnabledCookies = createDraftSafeSelector(getState, ({ cookies }) => cookies ?? []);

export const hasCookieEnabled = createDraftSafeSelector(getEnabledCookies, (cookies) => cookies.length > 0);

export const showCookieSettings = createDraftSafeSelector(
  getContext,
  isPublicDevice,
  hasCookieEnabled,
  (context, isPublic, hasCookies) => Boolean(context) && !isPublic && !hasCookies,
);

export const hasEnabledCookie = createDraftSafeSelector(
  getEnabledCookies,
  isPublicDevice,
  getProps,
  (cookies, isPublic, setting:CookiesSettings) => isPublic || cookies.includes(setting),
);

export const canCollectAnalyticsData = createDraftSafeSelector(
  getRoot,
  (root) => hasEnabledCookie(root, CookiesSettings.ANALYTICS),
);

export const isVehicleCheckEnabled = createDraftSafeSelector(
  getMFAData,
  getContext,
  (mfa, context) => mfa?.vehicleCheckEnabled ?? context?.vehicleCheckEnabled ?? false,
);

export const getTimezone = createDraftSafeSelector(
  getRoot,
  getSelectedDealerId,
  getTimezoneByDealerId,
);

export const getNotFoundSelfService = createDraftSafeSelector(getState, ({ notFound }) => notFound);

export const getCodeLoginError = createDraftSafeSelector(getState, (state) => state.codeLoginError as CodeLoginError);

export const getCodeRetryExpiration = createDraftSafeSelector(
  getCodeLoginError,
  getContext,
  (error, context) => error?.retryExpiration ?? context?.codeConnection?.retryExpiration,
);

export const hasValidQrCode = createDraftSafeSelector(
  isCodeLoginEnabled,
  getLoginData,
  (codeEnabled, loginData) => codeEnabled && loginData?.qrCode?.length === QR_CODE_LENGTH,
);

export const hasInvalidCodeError = createDraftSafeSelector(
  getCodeLoginError,
  (error) => Boolean(error),
);

export const isCodeFieldDisabled = createDraftSafeSelector(
  getCodeLoginError,
  getContext,
  (error, context) => {
    const retryExpiration = error?.retryExpiration ?? context?.codeConnection?.retryExpiration;
    return Boolean(retryExpiration && isFuture(new Date(retryExpiration)));
  },
);
